import { createContext, useContext } from 'react';

// Plattformens default-accent (Klinikverktygets gröna) – ersätts av tenantens accent_color från brand_settings.
export const KLINIO_DEFAULT_ACCENT = '#2C513F';

// Standardvärden när ingen brandkonfiguration finns
export const defaultBrand = {
  displayName: null,            // Klinikens / terapeutens namn
  logoUrl: null,                // Logotyp-URL
  iconUrl: null,                // Kvadratisk ikon (favicon/hemskärm) – används på tenantens egen adress
  tagline: null,                // Underrubrik / slogan
  accentColor: KLINIO_DEFAULT_ACCENT, // Accentfärg (hex) – tenant byter ut med sin färg
};

const PLATFORM_TITLE = 'Klinikverktyget';
const PLATFORM_ICONS = { icon: '/favicon.ico', apple: '/apple-touch-icon.png' };

// Värdnamn som INTE är en tenants egen adress (plattformens egna adresser + lokal utveckling).
export function isPlatformHost(host = window.location.hostname) {
  const h = (host || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.vercel.app') || h.endsWith('.localhost');
}

// Sätter fliktitel och ikoner efter varumärket. Utan brand (eller utan ikon) återställs
// plattformens egna. Anropas när varumärket laddas (egen adress, ?t=, inloggad klient).
// Bokstavsikon när kliniken saknar egen appikon: första bokstaven i namnet på klinikens färg.
// Ritas i webbläsaren (ingen lagring) så att plattformens ikon aldrig syns hos en tenant.
export function letterIconDataUrl(name, accent) {
  try {
    const c = document.createElement('canvas'); c.width = 180; c.height = 180;
    const x = c.getContext('2d');
    const bg = /^#[0-9a-f]{6}$/i.test(accent || '') ? accent : KLINIO_DEFAULT_ACCENT;
    x.fillStyle = bg; x.fillRect(0, 0, 180, 180);
    const n = parseInt(bg.slice(1), 16);
    const lum = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
    x.fillStyle = lum > 0.6 ? '#0F172A' : '#FFFFFF';
    x.font = '800 104px Manrope, Inter, system-ui, sans-serif';
    x.textAlign = 'center'; x.textBaseline = 'middle';
    x.fillText((name || '?').trim().charAt(0).toUpperCase(), 90, 96);
    return c.toDataURL('image/png');
  } catch (e) {
    return null;
  }
}

// Plattformens manifest-länk sparas när den kopplas bort hos en klinik, så att den kan återställas.
let platformManifestHref = null;

export function applyBrandToDocument(brand) {
  const name = brand?.displayName;
  document.title = name ? name : PLATFORM_TITLE;
  const icon = brand?.iconUrl || (name ? letterIconDataUrl(name, brand?.accentColor) : null);
  const set = (selector, href) => {
    let el = document.querySelector(selector);
    if (!el) return;
    el.setAttribute('href', href);
  };
  set('link[rel="icon"]', icon || PLATFORM_ICONS.icon);
  set('link[rel="apple-touch-icon"]', icon || PLATFORM_ICONS.apple);

  // Hemskärmsnamn: iOS/Android läser namnet ur manifest.json (alltid "Klinikverktyget")
  // eller apple-mobile-web-app-title. Hos en klinik sätts titeln till klinikens namn och
  // plattformens manifest kopplas bort, så att "Lägg till på hemskärmen" får klinikens namn.
  let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'apple-mobile-web-app-title');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', name || PLATFORM_TITLE);
  const manifest = document.querySelector('link[rel="manifest"]');
  if (name && manifest) {
    platformManifestHref = manifest.getAttribute('href');
    manifest.remove();
  } else if (!name && !manifest && platformManifestHref) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'manifest');
    link.setAttribute('href', platformManifestHref);
    document.head.appendChild(link);
  }
}

export const BrandContext = createContext(defaultBrand);

export function useBrand() {
  return useContext(BrandContext);
}
